import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.jsx'
import { initPersistentCache } from './lib/persistentCache'
import { initLocalDb } from './lib/localDb'
import { initNativePlatform } from './lib/nativePlatform'
import { initShellUpdateChecks } from './lib/shellUpdate'
import { startWatchdog } from './lib/connectionWatchdog'
import { initMutationQueue } from './lib/mutationQueue'
import { _rawPostInboxAction } from './hooks/useInboxIntelligence'
import { sendEmailFlush } from './lib/sendEmailFlush'
import { givePendingSendBack } from './lib/composeStore'
import { janetQueueDecideFlush } from './lib/janetQueueFlush'
import { reportClientError } from './lib/reportError'
import { installConsoleCapture } from './lib/consoleCapture'
import { withApiAuth } from './lib/apiAuth'
import { getAccessTokenWithTimeout } from './lib/authSession'
import { resetDemoCachesIfStale } from './lib/demoCacheReset'

// Install console + window error ring buffer FIRST so the Report Bug modal
// can attach recent errors to user-filed reports.
installConsoleCapture()

// ─── Skew handling ───────────────────────────────────────────────────────────
// Deploy skew (an open tab lazy-loading a chunk whose hash a newer deploy
// replaced) is handled by the vite:preloadError listener below: the failed
// import triggers one reload onto the fresh index.html. Do NOT reintroduce a
// client-set __vdpl pin here — pinning /assets to the deployment that served
// the HTML deadlocks after the next deploy (fresh HTML + pinned old assets →
// SPA rewrite serves HTML into the immutable asset cache → the entry module
// never runs again, including the code that would update the pin). The
// 2026-07-08 blank-page outage was exactly this; index.html carries the
// amnesty script that cleans up poisoned clients.
// Boot succeeded far enough to run the entry module — reset index.html's
// one-shot entry-recovery guard so a future poisoned-cache incident can
// self-heal again.
try { sessionStorage.removeItem('_entry_recovered') } catch { /* private mode */ }

// ─── Global /api fetch timeout ───────────────────────────────────────────────
// Every call to our own serverless endpoints (/api/*) goes through window.fetch
// but bypasses the Supabase client's fetch wrapper, so without this they have NO
// timeout — a slow or half-open endpoint freezes whatever UI awaits it, with no
// recovery short of a tab reload. Wrap window.fetch once so any same-origin
// /api/ request gets a default AbortController timeout unless the caller already
// passed its own signal. 90s is generous for slow AI endpoints (janet-draft,
// books-chat, drive-extract) while guaranteeing nothing hangs forever. Supabase
// queries hit *.supabase.co (not /api/) so they're untouched here — they have
// their own timeouts in lib/supabase.js.
;(function installApiFetchTimeout() {
  const nativeFetch = window.fetch.bind(window)
  const API_TIMEOUT_MS = 90_000
  const isApi = (url) => {
    try {
      const s = typeof url === 'string' ? url : (url?.url || '')
      if (!s) return false
      return new URL(s, window.location.origin).pathname.startsWith('/api/')
    } catch { return false }
  }
  // Same-origin /api calls also carry the signed-in session: the routes refuse
  // a request with no credential (src/lib/apiAuth.js says why).
  const origin = window.location.origin
  const getToken = () => getAccessTokenWithTimeout(3000)
  window.fetch = async function (input, init = {}) {
    if (!isApi(input)) return nativeFetch(input, init)
    const authed = await withApiAuth(input, init, { origin, getToken })
    if (authed.signal) return nativeFetch(input, authed)
    const controller = new AbortController()
    const timer = setTimeout(
      () => controller.abort(new DOMException('API request timeout', 'TimeoutError')),
      API_TIMEOUT_MS
    )
    return nativeFetch(input, { ...authed, signal: controller.signal }).finally(() => clearTimeout(timer))
  }
})()

// Global error listeners — report to system monitor so Janet can diagnose
// blank pages and JS crashes even when the React error boundary doesn't catch them.

const IGNORE_PATTERNS = [
  /AbortError/i,        // user cancelled a request — not a bug
  /NetworkError/i,      // offline — transient
  /Load failed/i,       // network / image load — transient
  /ResizeObserver/i,    // browser quirk — benign
  /Script error/i,      // cross-origin — can't do anything about it
]
const shouldIgnore = (msg) => IGNORE_PATTERNS.some(p => p.test(msg || ''))
// PGlite's IndexedDB file sync (emscripten IDBFS) throws this from inside its
// own callbacks when a save pass's transaction has already aborted (tab closing,
// storage pressure). The local mirror is a cache and the next pass reconciles
// it, so it is noise; matched on PGlite's own frames so an IndexedDB bug of ours
// would still be reported (System Monitor proposal be0adc6c, 2026-10-09).
const isPgliteSyncAbort = (msg, stack) =>
  /TransactionInactiveError/.test(msg || '') && /removeRemoteEntry|storeRemoteEntry|reconcile/.test(stack || '')

window.addEventListener('unhandledrejection', (e) => {
  const msg = e.reason?.message || String(e.reason) || 'Unhandled promise rejection'
  // Benign/transient noise (ResizeObserver, AbortError, offline) is neither
  // logged nor reported — it only buries real errors in the console.
  if (shouldIgnore(msg)) return
  // eslint-disable-next-line no-console
  console.error('[unhandledrejection]', msg, e.reason)
  reportClientError({
    error_message: msg,
    error_stack:   e.reason?.stack,
    error_type:    'unhandled_rejection',
    page_url:      window.location.href,
  })
})

window.addEventListener('error', (e) => {
  const msg = e.message || 'Window error'
  if (isPgliteSyncAbort(msg, e.error?.stack)) return
  if (shouldIgnore(msg)) {
    // ResizeObserver loop notifications spam the console hundreds of times;
    // stop the event so DevTools doesn't surface it as an issue either.
    e.stopImmediatePropagation?.()
    return
  }
  console.error('[window.onerror]', msg, e.error)
  if (e.error) {
    reportClientError({
      error_message: msg,
      error_stack:   e.error?.stack,
      error_type:    'window_error',
      page_url:      window.location.href,
    })
  }
})

// Vite fires this when a lazy chunk / CSS preload fails — usually a stale
// deploy (old hashed asset gone after a new build). It can happen outside
// React's render path, where no error boundary catches it. Reload to pull the
// fresh index.html, sharing the app boundary's reload budget so a persistent
// failure can't loop forever — once the budget is spent, let the error
// propagate so the boundary shows its manual-reload card.
window.addEventListener('vite:preloadError', (e) => {
  const KEY = '_chunk_reload_count'
  let count = 0
  try { count = Number(sessionStorage.getItem(KEY) || '0') } catch { /* ignore */ }
  if (count >= 3) return
  try { sessionStorage.setItem(KEY, String(count + 1)) } catch { /* ignore */ }
  e.preventDefault()
  // preventDefault makes Vite swallow the import error, so the pending lazy
  // import resolves `undefined` and React.lazy throws "Cannot read properties
  // of undefined (reading 'default')" before the reload below lands. This flag
  // lets loadErrorClass classify that throw as stale-deploy, not a code bug.
  window.__staleReloadPending = true
  console.warn('[vite:preloadError] stale asset — reloading', e.payload?.message)
  setTimeout(() => window.location.reload(), 1200) // let the CDN settle
})

// Offline mutation queue — drains queued actions when the browser reports
// online, on focus, or on explicit drainQueue() call.
//
// Dispatch by action name. Non-inbox actions (e.g. 'send_email', future
// 'approve_queued_draft') route to their own flush function taking just
// `args`; inbox actions keep their legacy `(action, args)` signature
// because they all hit the same /api/inbox-actions endpoint.
initMutationQueue({
  postAction: async (action, args) => {
    if (action === 'send_email') return sendEmailFlush(args)
    if (action === 'janet_queue_decide') return janetQueueDecideFlush(args)
    // Default: route to the inbox-actions endpoint
    return _rawPostInboxAction(action, args)
  },
  // A queued send that stops retrying gives its draft back instead of
  // sitting hidden behind its 'queued' marker.
  onFailed: (action, args, _err, permanent) => {
    if (action === 'send_email' && args?.sendKey) givePendingSendBack(args.sendKey, permanent ? 'rejected' : 'unconfirmed', _err?.message)
  },
  onChange: () => {
    // Dispatch a window event so any UI badge (InboxIntelligencePage) can
    // pick up queue count changes without prop-drilling.
    try { window.dispatchEvent(new CustomEvent('mutation-queue-change')) }
    catch { /* older browsers */ }
  },
})

// ─── Boot render gate ─────────────────────────────────────────────────────────
// Render as soon as the offline IDB cache is hydrated so every hook's first
// getCached() call sees real data, not undefined.  IDB reads are fast (< 100 ms
// with data; near-instant when empty), so gating render on this prevents the
// "flash blank then fill in" bug without a perceptible delay.
//
// CRITICAL: PGlite (initLocalDb) must NOT gate render.  It loads its entire data
// directory out of IndexedDB into WASM memory on every boot, and that store
// grows with the local mirror — once it reached ~1 GB the load took 20–50 s,
// during which the whole app was blank.  PGlite is only an offline fallback:
// until it resolves, getLocalDb() returns null and every hook falls through to
// Supabase, so the app is fully functional.  So we kick it off fire-and-forget
// in the background and never let it block (or hang) the first paint.
const BOOT_HYDRATE_TIMEOUT_MS = 3000
// The timer must be cancelled when the promise settles first — an un-cancelled
// timer fired its "not ready" warning at 3s on EVERY boot, even ones where
// hydration finished in milliseconds, so the log could never tell a healthy
// boot from a wedged one.
const withTimeout = (promise, ms, label) => {
  let timer
  return Promise.race([
    promise.finally(() => clearTimeout(timer)),
    new Promise(resolve => {
      timer = setTimeout(() => {
        console.warn(`[boot] ${label} not ready after ${ms}ms — rendering without it`)
        resolve('timeout')
      }, ms)
    }),
  ])
}

// Background: PGlite initializes and populates _db when ready. Never gates
// render — until it resolves, getLocalDb() is null and every hook falls
// through to Supabase, so the app is fully functional.
const startLocalDb = () => {
  initLocalDb()
    .then(() => console.info('[localDb] PGlite ready'))
    .catch(err => {
      // PGlite unavailable (private browsing, WASM blocked, slow/large store) —
      // app still works via Supabase directly; watchdog/sync no-op until ready.
      console.warn('[localDb] PGlite unavailable, continuing without local DB:', err?.message)
    })
}

// Demo scenario-content version check — BEFORE initPersistentCache()/
// startLocalDb(), and before createRoot(), per the invariant it exists to
// enforce: a reseed that moves the primary scenario (Tidewater → Eighty Days)
// must invalidate a returning visitor's cached business-list/entity reads
// before anything hydrates from them, not after DemoProvider mounts over
// already-stale data. A no-op outside demo mode. resetDemoCachesIfStale()
// bounds its own IndexedDB clear calls (never hangs this await indefinitely)
// and never marks itself complete on a failed/timed-out clear — the .catch()
// here is belt-and-suspenders against anything escaping that, so a
// demo-cache problem can never itself block boot; the fallback result on
// catch carries no `skip`, so hydration below proceeds unfiltered exactly as
// it did before this mechanism existed.
async function bootstrap() {
  const demoCacheResult = await resetDemoCachesIfStale().catch((err) => {
    console.warn('[demoCacheReset] failed, continuing without a cache check:', err?.message)
    return null
  })

  // Desktop shell: the mirror is on-disk behind the IPC bridge — no IndexedDB
  // contention with Dexie (the starvation problem below is browser-only), and
  // local-first paints want it ready by the first route render. Start it NOW.
  if (window.chatroneDesktop?.db) startLocalDb()

  // Render once the IDB cache is hydrated — bounded so a wedged IDB can't hang boot.
  //
  // Browser only: PGlite (initLocalDb) starts AFTER this gate settles, not
  // before: both stores live in IndexedDB, and PGlite reading its whole data
  // directory at boot starved the (tiny) Dexie hydration read past the 3s
  // timeout on every load — so the render gate always fired the "not ready" path
  // and the warm-cache first paint never happened. Sequencing costs PGlite a few
  // hundred ms of start delay; it's a background fallback, so nothing
  // user-facing waits on it.
  //
  // `skip` (present only when the demo cache reset ran and could NOT prove
  // its IndexedDB clear finished) excludes exactly the affected keys from
  // THIS boot's hydration — a failed/timed-out clear leaving the version
  // marker behind only guarantees a retry NEXT boot; without this, the same
  // stale row would be read straight back out of disk a moment later by the
  // very hydration this call performs.
  await withTimeout(
    initPersistentCache({ skip: demoCacheResult?.skip })
      .then(result => {
        if (result?.hydrated > 0) {
          console.info(`[persistentCache] hydrated ${result.hydrated} entries from IDB`)
        }
      })
      .catch(err => {
        console.warn('[persistentCache] init failed, continuing without offline cache:', err?.message)
      }),
    BOOT_HYDRATE_TIMEOUT_MS,
    'persistentCache',
  )
    .finally(() => {
      createRoot(document.getElementById('root')).render(
        <StrictMode>
          <App />
        </StrictMode>,
      )

      // Native (Capacitor/iOS) shell bootstrap — status bar, splash, deep links,
      // safe-area classes. No-op on web/desktop.
      initNativePlatform()

      // Thin-client shells (iOS + desktop) load the live site, so a shipped web
      // fix can lay dormant behind a backgrounded/cached webview until a hard
      // reload. Re-check on cold start + return-to-foreground and reload when a
      // newer deploy exists. No-op on web (a tab reload already gets fresh HTML).
      initShellUpdateChecks()

      // Start the connection watchdog AFTER React mounts so the initial sync
      // runs in the background and doesn't block the first render.
      startWatchdog()

      // Browser: PGlite starts after the render gate (see comment above).
      // Desktop already started it before the gate.
      if (!window.chatroneDesktop?.db) startLocalDb()
    })
}

bootstrap()
